/**
 * AI'ın ürettiği HTML raporlar.
 *
 * İki yoldan geliyorlar ve ikisi de yakalanmalı:
 *   1. Canlı akışta `Write` aracı bir `.html` dosyası yazdığında (bkz. aiStream).
 *   2. Kayıtlı sohbet yeniden açıldığında: sunucu raporu mesaj metnine ```html bloğu
 *      olarak katıyor (ClaudeCodeController), yani geçmişte rapor bir kod bloğu.
 *
 * Ham HTML'i sohbet balonunda göstermek okunmaz bir duvar demek; blok metinden
 * çıkarılıp yerine kısa bir not bırakılıyor, rapor ise kart olarak açılıyor.
 */
import type { ReportArtifact } from '@/shared/types/aiChat';

/** Mesaj metnindeki ```html bloklarını rapora çevirir. */
export function extractArtifacts(content: string): { cleaned: string; artifacts: ReportArtifact[] } {
  if (!content || !/```html/i.test(content)) return { cleaned: content, artifacts: [] };

  const artifacts: ReportArtifact[] = [];
  let index = 0;

  const cleaned = content.replace(/```html\s*([\s\S]*?)```/gi, (_match, code: string) => {
    const html = (code ?? '').trim();
    if (!html) return '';
    index += 1;
    const title = deriveTitle(html, index);
    artifacts.push({ title, fileName: sanitizeFileName(title) || `rapor_${index}`, code: html });
    return `\n\n*📄 ${title}*\n`;
  });

  return { cleaned: cleaned.trim(), artifacts };
}

/** Rapor başlığı: belgenin kendi başlığı, yoksa ilk H1, yoksa sıra numarası. */
export function deriveTitle(html: string, index = 1): string {
  const title = matchFirst(html, /<title[^>]*>([\s\S]*?)<\/title>/i);
  if (title) return stripTags(title);
  const h1 = matchFirst(html, /<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (h1) return stripTags(h1);
  return `Rapor ${index}`;
}

function matchFirst(input: string, regex: RegExp): string | null {
  const match = input.match(regex);
  return match && match[1] ? match[1].trim() : null;
}

function stripTags(input: string): string {
  return input
    .replace(/<[^>]+>/g, '')
    .trim()
    .slice(0, 80);
}

/** Dosya adı: Türkçe harfler korunur, ayraçlar alt çizgiye iner. */
export function sanitizeFileName(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9ğüşöçı\s-]/gi, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 50);
}

/**
 * Raporu indirilebilir dosyaya çevirir.
 *
 * Tarayıcıya bırakılan indirme, uygulamanın sekmesini terk etmeden çalışsın diye
 * blob + gizli bağlantı ile yapılıyor.
 */
export function downloadHtml(artifact: ReportArtifact) {
  const blob = new Blob([artifact.code], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${artifact.fileName || 'rapor'}.html`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Bazı tarayıcılar indirmeyi hemen başlatmıyor; URL biraz yaşasın.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
